import { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { ChevronLeft, ChevronRight, FilePen, QrCode, Check } from 'lucide-react'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

// Silenciar los warnings cosmeticos de pdf.js sobre fuentes TrueType
// con instrucciones no estandar ('Warning: TT: undefined function: N').
// El sanitizador de pdf.js corrige las fuentes automaticamente; los
// warnings solo llenan la consola sin aportar info accionable.
// 0 = ERRORS only, 1 = WARNINGS (default), 5 = INFOS.
const PDFJS_DOCUMENT_OPTIONS = { verbosity: 0 }

export interface ViewportRect {
  pageNumber: number
  x: number
  y: number
  width: number
  height: number
  pageRenderedWidth: number
  pageRenderedHeight: number
}

export type Slot = 'signature' | 'qr'

interface Props {
  pdfFile: File
  mode: 'text' | 'qr' | 'text_and_qr' | 'drawn' | 'drawn_and_qr'
  activeSlot: Slot
  onActiveSlotChange: (s: Slot) => void
  signatureRect: ViewportRect | null
  qrRect: ViewportRect | null
  onSignatureRectChange: (r: ViewportRect | null) => void
  onQrRectChange: (r: ViewportRect | null) => void
  previewContent: {
    text: string
    qrDataUrl: string | null
    drawnImageDataUrl?: string | null
    verificationUrlDisplay: string
  }
}

const MIN_RECT_PX = 30

export function PdfPreviewWithPlacement({
  pdfFile, mode,
  activeSlot, onActiveSlotChange,
  signatureRect, qrRect,
  onSignatureRectChange, onQrRectChange,
  previewContent,
}: Props) {
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageWidth, setPageWidth] = useState(0)
  const [pageHeight, setPageHeight] = useState(0)

  const needsSignature = mode === 'text' || mode === 'text_and_qr' || mode === 'drawn' || mode === 'drawn_and_qr'
  const needsQr = mode === 'qr' || mode === 'text_and_qr' || mode === 'drawn_and_qr'
  const isDrawnMode = mode === 'drawn' || mode === 'drawn_and_qr'

  // Slot activo segun el modo (forzar single-slot modes)
  const effectiveSlot: Slot = (mode === 'text' || mode === 'drawn') ? 'signature' : mode === 'qr' ? 'qr' : activeSlot

  const activeRect = effectiveSlot === 'signature' ? signatureRect : qrRect
  const otherRect = effectiveSlot === 'signature' ? qrRect : signatureRect
  const otherSlot: Slot = effectiveSlot === 'signature' ? 'qr' : 'signature'
  const setActiveRect = effectiveSlot === 'signature' ? onSignatureRectChange : onQrRectChange

  type Action =
    | { kind: 'idle' }
    | { kind: 'drawing'; start: { x: number; y: number }; current: { x: number; y: number } }
    | { kind: 'moving'; offset: { x: number; y: number }; rect: { x: number; y: number; width: number; height: number } }

  const [action, setAction] = useState<Action>({ kind: 'idle' })
  const [hoverInside, setHoverInside] = useState(false)

  const overlayRef = useRef<HTMLDivElement>(null)

  // Resetear todo cuando cambia el archivo
  useEffect(() => {
    setAction({ kind: 'idle' })
  }, [pdfFile])

  const handlePageRender = ({ width, height }: { width: number; height: number }) => {
    setPageWidth(width)
    setPageHeight(height)
  }

  const getCoords = (e: React.MouseEvent) => {
    const rect = overlayRef.current!.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(e.clientY - rect.top, rect.height)),
    }
  }

  // Solo permitir interactuar con activeRect en la pagina actual
  const activeRectOnThisPage = activeRect && activeRect.pageNumber === pageNumber ? activeRect : null
  const otherRectOnThisPage = otherRect && otherRect.pageNumber === pageNumber ? otherRect : null

  const isInsideActive = (c: { x: number; y: number }) =>
    !!activeRectOnThisPage &&
    c.x >= activeRectOnThisPage.x && c.x <= activeRectOnThisPage.x + activeRectOnThisPage.width &&
    c.y >= activeRectOnThisPage.y && c.y <= activeRectOnThisPage.y + activeRectOnThisPage.height

  const commit = (rect: { x: number; y: number; width: number; height: number }) => {
    if (rect.width < MIN_RECT_PX || rect.height < MIN_RECT_PX) {
      setActiveRect(null)
      return
    }
    const clampedX = Math.max(0, Math.min(rect.x, pageWidth - rect.width))
    const clampedY = Math.max(0, Math.min(rect.y, pageHeight - rect.height))
    setActiveRect({
      pageNumber,
      x: clampedX, y: clampedY,
      width: rect.width, height: rect.height,
      pageRenderedWidth: pageWidth, pageRenderedHeight: pageHeight,
    })
  }

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const c = getCoords(e)
    if (isInsideActive(c) && activeRectOnThisPage) {
      setAction({
        kind: 'moving',
        offset: { x: c.x - activeRectOnThisPage.x, y: c.y - activeRectOnThisPage.y },
        rect: { x: activeRectOnThisPage.x, y: activeRectOnThisPage.y, width: activeRectOnThisPage.width, height: activeRectOnThisPage.height },
      })
    } else {
      setAction({ kind: 'drawing', start: c, current: c })
      setActiveRect(null)
    }
  }

  const onMouseMove = (e: React.MouseEvent) => {
    const c = getCoords(e)
    if (action.kind === 'idle') {
      setHoverInside(isInsideActive(c))
      return
    }
    if (action.kind === 'drawing') {
      setAction({ ...action, current: c })
    } else if (action.kind === 'moving') {
      setAction({
        ...action,
        rect: { ...action.rect, x: c.x - action.offset.x, y: c.y - action.offset.y },
      })
    }
  }

  const onMouseUp = () => {
    if (action.kind === 'drawing') {
      const { start, current } = action
      commit({
        x: Math.min(start.x, current.x),
        y: Math.min(start.y, current.y),
        width: Math.abs(current.x - start.x),
        height: Math.abs(current.y - start.y),
      })
      setAction({ kind: 'idle' })
    } else if (action.kind === 'moving') {
      commit(action.rect)
      setAction({ kind: 'idle' })
    }
  }

  const drawPreview = (() => {
    if (action.kind === 'drawing') {
      const { start, current } = action
      return {
        x: Math.min(start.x, current.x), y: Math.min(start.y, current.y),
        width: Math.abs(current.x - start.x), height: Math.abs(current.y - start.y),
      }
    }
    if (action.kind === 'moving') return action.rect
    return activeRectOnThisPage
  })()

  const cursor = (() => {
    if (action.kind === 'moving') return 'grabbing'
    if (action.kind === 'drawing') return 'crosshair'
    if (hoverInside && activeRectOnThisPage) return 'move'
    return 'crosshair'
  })()

  // Step indicator solo en modo combinado
  const stepInfo = mode === 'text_and_qr'
    ? { current: effectiveSlot === 'signature' ? 1 : 2, total: 2 }
    : null

  const slotInstructions = effectiveSlot === 'signature'
    ? 'Marcá un rectángulo donde va la firma'
    : 'Marcá un rectángulo donde va el QR (mín. ~80x80 pt para que sea escaneable)'

  return (
    <div className="bg-white dark:bg-legal-800 rounded-xl shadow-md border border-legal-100 dark:border-legal-700 p-6">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-legal-900 dark:text-white">
            4. Posicionamiento
            {stepInfo && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                paso {stepInfo.current} de {stepInfo.total}
              </span>
            )}
          </h2>
          {mode === 'text_and_qr' && (
            <div className="flex gap-2">
              <SlotTab
                active={effectiveSlot === 'signature'}
                done={!!signatureRect}
                onClick={() => onActiveSlotChange('signature')}
                icon={<FilePen size={14} />}
                label="Firma"
              />
              <SlotTab
                active={effectiveSlot === 'qr'}
                done={!!qrRect}
                onClick={() => onActiveSlotChange('qr')}
                icon={<QrCode size={14} />}
                label="QR"
              />
            </div>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {slotInstructions}. Después podés moverlo arrastrándolo desde adentro o redibujarlo afuera.
          {otherRectOnThisPage && ' El otro rectángulo (atenuado) ya está colocado.'}
        </p>
      </div>

      {numPages > 0 && (
        <div className="flex items-center justify-center gap-3 mb-3">
          <button
            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
            disabled={pageNumber === 1}
            className="p-2 rounded-lg border border-legal-200 dark:border-legal-600 disabled:opacity-30"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-legal-700 dark:text-legal-300">
            Página {pageNumber} de {numPages}
          </span>
          <button
            onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
            disabled={pageNumber === numPages}
            className="p-2 rounded-lg border border-legal-200 dark:border-legal-600 disabled:opacity-30"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      <div className="overflow-auto max-h-[70vh] border border-legal-200 dark:border-legal-700 rounded-lg flex justify-center bg-legal-50 dark:bg-legal-900">
        <div style={{ position: 'relative', width: 'max-content' }}>
          <Document
            file={pdfFile}
            options={PDFJS_DOCUMENT_OPTIONS}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<div className="p-10 text-sm text-gray-500">Cargando PDF...</div>}
            error={<div className="p-10 text-sm text-red-500">Error al cargar PDF</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={Math.min(window.innerWidth - 100, 700)}
              onRenderSuccess={handlePageRender}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>

          {pageWidth > 0 && (
            <div
              ref={overlayRef}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={() => { onMouseUp(); setHoverInside(false) }}
              style={{
                position: 'absolute', top: 0, left: 0,
                width: pageWidth, height: pageHeight,
                cursor, userSelect: 'none',
              }}
            >
              {/* Etiqueta del rect ACTIVO (arriba a la izquierda, fuera del rect) */}
              {activeRectOnThisPage && action.kind === 'idle' && (
                <span style={{
                  position: 'absolute',
                  left: activeRectOnThisPage.x,
                  top: Math.max(0, activeRectOnThisPage.y - 16),
                  background: '#3b82f6', color: '#fff',
                  fontSize: 9, padding: '1px 5px', borderRadius: 3,
                  pointerEvents: 'none',
                  fontWeight: 600,
                }}>
                  {effectiveSlot === 'signature' ? 'Firma colocada' : 'QR colocado'}
                </span>
              )}

              {/* Otro rect ya colocado: atenuado, no editable. Etiqueta
                  fuera del rect para no tapar el contenido. */}
              {otherRectOnThisPage && (
                <>
                  <div style={{
                    position: 'absolute',
                    left: otherRectOnThisPage.x, top: otherRectOnThisPage.y,
                    width: otherRectOnThisPage.width, height: otherRectOnThisPage.height,
                    border: '1px dashed rgba(100,116,139,0.6)',
                    pointerEvents: 'none',
                    boxSizing: 'border-box',
                  }}>
                    <StampPreview
                      rect={{ x: 0, y: 0, width: otherRectOnThisPage.width, height: otherRectOnThisPage.height }}
                      slotType={otherSlot}
                      isDrawn={otherSlot === 'signature' && isDrawnMode}
                      text={previewContent.text}
                      qrDataUrl={previewContent.qrDataUrl}
                      drawnImageDataUrl={previewContent.drawnImageDataUrl ?? null}
                      urlDisplay={previewContent.verificationUrlDisplay}
                      dimmed
                    />
                  </div>
                  <span style={{
                    position: 'absolute',
                    left: otherRectOnThisPage.x,
                    top: Math.max(0, otherRectOnThisPage.y - 16),
                    background: '#64748b', color: '#fff',
                    fontSize: 9, padding: '1px 5px', borderRadius: 3,
                    pointerEvents: 'none',
                    fontWeight: 600,
                  }}>
                    {otherSlot === 'signature' ? 'Firma colocada' : 'QR colocado'}
                  </span>
                </>
              )}

              {/* Slot activo: dashed border durante drag */}
              {drawPreview && drawPreview.width > 0 && drawPreview.height > 0 && (
                <>
                  {action.kind !== 'idle' && (
                    <div style={{
                      position: 'absolute',
                      left: drawPreview.x, top: drawPreview.y,
                      width: drawPreview.width, height: drawPreview.height,
                      border: '1.5px dashed #3b82f6',
                      pointerEvents: 'none', boxSizing: 'border-box',
                    }} />
                  )}
                  <div style={{
                    position: 'absolute',
                    left: drawPreview.x, top: drawPreview.y,
                    width: drawPreview.width, height: drawPreview.height,
                    pointerEvents: 'none',
                  }}>
                    <StampPreview
                      rect={{ x: 0, y: 0, width: drawPreview.width, height: drawPreview.height }}
                      slotType={effectiveSlot}
                      isDrawn={effectiveSlot === 'signature' && isDrawnMode}
                      text={previewContent.text}
                      qrDataUrl={previewContent.qrDataUrl}
                      drawnImageDataUrl={previewContent.drawnImageDataUrl ?? null}
                      urlDisplay={previewContent.verificationUrlDisplay}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Boton para avanzar al siguiente paso en modo combinado */}
      {mode === 'text_and_qr' && activeRect && effectiveSlot === 'signature' && !qrRect && (
        <button
          onClick={() => onActiveSlotChange('qr')}
          className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
        >
          <Check size={16} />
          Continuar al paso 2: posicionar el QR
        </button>
      )}

      {/* Status del slot activo */}
      <div className="mt-3 text-xs text-legal-600 dark:text-legal-400 space-y-1">
        {needsSignature && (
          <div className="flex items-center gap-2">
            <span className={signatureRect ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
              {signatureRect ? '✓' : '○'}
            </span>
            <span>Firma: {signatureRect
              ? `${Math.round(signatureRect.width)}×${Math.round(signatureRect.height)} px en pág. ${signatureRect.pageNumber}`
              : <span className="text-gray-500">pendiente</span>
            }</span>
          </div>
        )}
        {needsQr && (
          <div className="flex items-center gap-2">
            <span className={qrRect ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
              {qrRect ? '✓' : '○'}
            </span>
            <span>QR: {qrRect
              ? `${Math.round(qrRect.width)}×${Math.round(qrRect.height)} px en pág. ${qrRect.pageNumber}`
              : <span className="text-gray-500">pendiente</span>
            }</span>
          </div>
        )}
      </div>
    </div>
  )
}

function SlotTab({ active, done, onClick, icon, label }: {
  active: boolean; done: boolean; onClick: () => void; icon: React.ReactNode; label: string
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
        active
          ? 'bg-primary-600 border-primary-600 text-white'
          : done
            ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-800 text-green-700 dark:text-green-300'
            : 'bg-white dark:bg-legal-700 border-legal-200 dark:border-legal-600 text-legal-700 dark:text-legal-200 hover:border-primary-400'
      }`}
    >
      {icon}
      {label}
      {done && !active && <Check size={12} />}
    </button>
  )
}

// Preview WYSIWYG del stamp dentro del rectangulo del usuario.
// Layout debe coincidir con stampOntoPdfAtCoords (un solo elemento por rect).
function StampPreview({
  rect, slotType, isDrawn, text, qrDataUrl, drawnImageDataUrl, urlDisplay, dimmed,
}: {
  rect: { x: number; y: number; width: number; height: number }
  slotType: 'signature' | 'qr'
  isDrawn?: boolean
  text: string
  qrDataUrl: string | null
  drawnImageDataUrl?: string | null
  urlDisplay: string
  dimmed?: boolean
}) {
  const w = rect.width, h = rect.height
  const urlFontSize = Math.max(6, Math.min(8, h * 0.085))
  const urlBlockHeight = urlFontSize * 2 + 4
  const opacity = dimmed ? 0.4 : 1
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: rect.x, top: rect.y,
    width: w, height: h,
    pointerEvents: 'none',
    boxSizing: 'border-box',
    overflow: 'hidden',
    opacity,
  }

  const urlBlock = (
    <div style={{ width: '100%', padding: '0 4px', lineHeight: 1.05 }}>
      <div style={{ fontSize: urlFontSize, fontWeight: 700, color: '#1e3a8a' }}>Firmado con SignWiz</div>
      <div style={{ fontSize: urlFontSize, color: '#64748b', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        {urlDisplay}
      </div>
    </div>
  )

  if (slotType === 'signature') {
    const cursiveHeight = h - urlBlockHeight
    return (
      <div style={baseStyle}>
        {isDrawn && drawnImageDataUrl ? (
          <img
            src={drawnImageDataUrl}
            alt="Firma dibujada"
            style={{ width: '100%', height: cursiveHeight, objectFit: 'contain', objectPosition: 'left center' }}
          />
        ) : isDrawn ? (
          <div style={{
            width: '100%', height: cursiveHeight,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 4,
            color: '#94a3b8', fontStyle: 'italic', fontSize: 11,
          }}>
            (dibujá tu firma arriba)
          </div>
        ) : (
          <div style={{
            width: '100%', height: cursiveHeight,
            fontFamily: '"Dancing Script", "Brush Script MT", cursive',
            color: '#0a2540',
            fontSize: Math.max(12, cursiveHeight * 0.75),
            display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
            paddingLeft: 4,
            whiteSpace: 'nowrap', overflow: 'hidden', lineHeight: 1,
          }}>
            {text || 'Firma'}
          </div>
        )}
        {urlBlock}
      </div>
    )
  }

  // QR alineado al borde izquierdo del rect, URL al ancho del rect entero
  const qrSize = Math.min(w, h - urlBlockHeight - 2)
  return (
    <div style={baseStyle}>
      <div style={{
        position: 'absolute', left: 0, top: 0,
        width: qrSize, height: qrSize,
      }}>
        {qrDataUrl
          ? <img src={qrDataUrl} alt="QR" style={{ width: '100%', height: '100%', background: '#fff' }} />
          : <div style={{ width: '100%', height: '100%', background: '#e2e8f0' }} />
        }
      </div>
      <div style={{
        position: 'absolute', left: 0, bottom: 0,
        width: '100%', padding: '0 2px', lineHeight: 1.05,
      }}>
        <div style={{ fontSize: urlFontSize, fontWeight: 700, color: '#1e3a8a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Firmado con SignWiz</div>
        <div style={{ fontSize: urlFontSize, color: '#64748b', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
          {urlDisplay}
        </div>
      </div>
    </div>
  )
}
